/* eslint-disable no-prototype-builtins */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector} from 'react-redux';
import {
  Divider,
  TextField,
  Grid,
  Switch,
  Button,
  Icon,
  FormHelperText
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import JoditEditor from 'jodit-react';
import { changeAboutUsPageConfigConfig } from 'redux/actions/store_configs';
import { ImageGalleryDialog, BackdropCircular } from 'components';
import { uploadFileTypeUnsupported, uploadFileSizeLimit } from 'redux/actions/general';
import { assetManagerUrl } from 'configs';
import {
  uploadProviderImageApi,
  getAssetManagerImagesApi
} from 'api';
import SelectOwnImage from './SelectOwnImage';


const useStyles = makeStyles({
  customized_error: {
    '& .jodit-container:not(.jodit_inline)': {
      border: '1px solid #FF3D57'
    },
    '& .jodit-placeholder': {
      color: '#FF3D57'
    }
  },
});

const config: unknown = {
  'minHeight': 100,
  'showXPathInStatusbar': false,
  'spellcheck': false,
  'askBeforePasteHTML': false,
  'askBeforePasteFromWord': false,
  'toolbar': false,
  'showCharsCounter': false,
  'showWordsCounter': false,
  'enter': 'BR',
  'placeholder': '*Text'
};

const AboutUsPageProviderBlock = ({values, onChange, errors}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { provider_section } = values;
  const [openImageDialog, toggleOpenImageDialog] = useState(false);
  const [ownImagesTagIds, setOwnImagesTagIds] = useState('');

  const {
    assetCategoryTags,
    assetImagesData,
    currentCategoryId,
    providerUploadedImageData,
    uploadingProviderImage
  } = useSelector((state: IReduxState) => state.asset_manager);

  const {profile: {stores = []}} = useSelector((state: IReduxState) => state.user);

  const storeCode = stores.length && stores[0]?.storeCode;

  const providerGeneralTagData = assetCategoryTags.find((tag) => tag.tagCode === 'about_us_page_provider_section');
  const privateTagData = assetCategoryTags.find((tag) => tag.tagCode === 'private');
  const storeCodeTagData = assetCategoryTags.find((tag) => tag.tagCode === `${storeCode}`);

  useEffect(() => {
    if (Object.keys(providerUploadedImageData).length) {
      handleSelectImage(providerUploadedImageData, 'store_info/about_us_page/provider_image');
    }
  }, [providerUploadedImageData]);

  useEffect(() => {
    if (providerGeneralTagData && privateTagData && storeCodeTagData) {
      setOwnImagesTagIds(`${providerGeneralTagData.id}, ${privateTagData.id}, ${storeCodeTagData.id}`);
    }
  }, [providerGeneralTagData, privateTagData, storeCodeTagData]);

  useEffect(() => {
    getAssetManagerImagesApi({
      dispatch,
      limit: 20,
      page: 1,
      tagIds: ownImagesTagIds,
      isPrivate: true
    });
  }, [ownImagesTagIds]);

  const handleChange = (event) => {
    const {name, checked, type, value} = event.target;
    dispatch(changeAboutUsPageConfigConfig(name, checked, type, value, 'provider_section'));
  };

  const handleChangeContent = (value, name) => {
    dispatch(changeAboutUsPageConfigConfig(name, null, 'textarea', value, 'provider_section'));
  };

  const handleSelectImage = (data, selectImageConfigPath) => {
    const value = `${assetManagerUrl}/${data.webPath}`;
    dispatch(changeAboutUsPageConfigConfig(
      selectImageConfigPath,
      null,
      'text',
      value,
      'provider_section'
    ));
    onChange(`provider_section[${selectImageConfigPath}]`, value);
  };

  const handleUpLoadImage = (event) => {
    const {files, value} = event.target;
    const formData = new FormData();

    if (files.length) {
      const fileTypeDotIndexPosition = value.lastIndexOf('.') + 1;
      const slicedFileTypeFromFilePath = value.slice(fileTypeDotIndexPosition);
      const acceptableFormats = 'png|jpeg';
      const hasImageFile = new RegExp(`(image\\/(${acceptableFormats}))`, 'gi');

      if (!hasImageFile.test(files[0].type) && !hasImageFile.test(slicedFileTypeFromFilePath)) {
        dispatch(uploadFileTypeUnsupported());
      } else if (files[0].size > 524288) {
        dispatch(uploadFileSizeLimit('512kB'));
      } else {
        formData.append('image', files[0]);

        if (ownImagesTagIds.length) {
          formData.append('tags', `about_us_page_provider_section,private,${storeCode}`);
        } else {
          const tags = [
            providerGeneralTagData.tagCode,
            privateTagData.tagCode,
            {
              label: storeCode,
              tagCode: storeCode,
              category: currentCategoryId
            }
          ];
          formData.append('tags', JSON.stringify(tags));
        }

        const imageData = uploadProviderImageApi({dispatch, formData, isAddingNewTag: !ownImagesTagIds.length});

        if (imageData) {
          handleSelectImage(imageData, 'store_info/about_us_page/provider_image');
        }
      }
    }
  };

  const isProviderMainHeadingError = errors?.provider_section?.hasOwnProperty('store_info/about_us_page/provider_main_heading');
  const isProviderContentError = errors?.provider_section?.hasOwnProperty('store_info/about_us_page/provider_content');

  return (
    <div>
      <Grid container spacing={3} className="mb-4">
        <Grid item xs={12} sm={2} className="my-2 ml-4">Enable</Grid>
        <Grid item xs={12} sm={9}>
          <Switch
            color="primary"
            name={'store_info/about_us_page/is_provider_section_enabled'}
            checked={provider_section['store_info/about_us_page/is_provider_section_enabled'] == 1}
            onChange={(event) => {
              onChange('provider_section[\'store_info/about_us_page/is_provider_section_enabled\']', event.target.checked ? 1 : 0);
              handleChange(event);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">
          Provider Image<span className="text-error">*</span>
        </Grid>
        <Grid item xs={12} sm={9}>
          <div>
            <Button
              className="w-full max-w-300 p-0"
              variant="outlined"
              color="primary"
              size="large"
            >
              <label htmlFor="upload-provider-image" className="flex items-center w-full justify-center px-5 py-2">
                <Icon className="mr-2">upload</Icon>
                Upload Own Image
              </label>
            </Button>
            <input
              className="hidden"
              onChange={handleUpLoadImage}
              onClick={(event) => (event.target as HTMLInputElement).value = null}
              id="upload-provider-image"
              type="file"
              accept="image/png, image/jpeg"
            />
          </div>
          <FormHelperText className="mt-2">
            Files supported: png, jpeg, jpg. File size should be less than 512kB
          </FormHelperText>
          <Button
            className="w-full max-w-300 mt-2 mr-2"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => toggleOpenImageDialog(true)}
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select From Library
          </Button>
          <SelectOwnImage
            title="Select Provider Image From Own Images"
            imagesTagIds={ownImagesTagIds}
            isOwnImages={assetImagesData?.[ownImagesTagIds]?.items?.length > 0}
            selectImageConfigPath="store_info/about_us_page/provider_image"
            handleSelectImage={handleSelectImage}
            cols={4}
            rowHeight={240}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">
          Provider Main Heading Text <span className="text-error">*</span>
        </Grid>
        <Grid item xs={12} sm={9}>
          <TextField
            required
            fullWidth
            variant="outlined"
            name={'store_info/about_us_page/provider_main_heading'}
            label="Text"
            value={provider_section['store_info/about_us_page/provider_main_heading']}
            onChange={(event) => {
              onChange('provider_section[\'store_info/about_us_page/provider_main_heading\']', event.target.value);
              handleChange(event);
            }}
            inputProps={{
              shrink: 'true'
            }}
            helperText={isProviderMainHeadingError
              && errors.provider_section['store_info/about_us_page/provider_main_heading']}
            error={isProviderMainHeadingError}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Provider Secondary Heading Text</Grid>
        <Grid item xs={12} sm={9}>
          <TextField
            fullWidth
            variant="outlined"
            name={'store_info/about_us_page/provider_secondary_heading'}
            label="Text"
            value={provider_section['store_info/about_us_page/provider_secondary_heading']}
            onChange={(event) => {
              onChange('provider_section[\'store_info/about_us_page/provider_secondary_heading\']', event.target.value);
              handleChange(event);
            }}
            inputProps={{
              shrink: 'true'
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="ml-4">
          Provider Content Text <span className="text-error">*</span>
        </Grid>
        <Grid item xs={12} sm={9}>
          <div className={isProviderContentError ? classes.customized_error : ''}>
            <JoditEditor
              value={provider_section['store_info/about_us_page/provider_content']}
              config={config}
              onChange={(newContent) => {
                const content = newContent.replace(/style="(.*?)"/gm, '');
                onChange('provider_section[\'store_info/about_us_page/provider_content\']', content);
                handleChangeContent(content, 'store_info/about_us_page/provider_content');
              }}
              onBlur={(newContent) => {
                const content = newContent.replace(/style="(.*?)"/gm, '');
                onChange('provider_section[\'store_info/about_us_page/provider_content\']', content);
                handleChangeContent(content, 'store_info/about_us_page/provider_content');
              }}
            />
          </div>
          {isProviderContentError &&
            <FormHelperText error className="ml-3">
              {errors.provider_section['store_info/about_us_page/provider_content']}
            </FormHelperText>
          }
        </Grid>
      </Grid>
      {providerGeneralTagData?.id && openImageDialog &&
      <ImageGalleryDialog
        title="Select Provider Image From Library"
        open={openImageDialog}
        setOpen={toggleOpenImageDialog}
        onImageSelect={(data) => handleSelectImage(data, 'store_info/about_us_page/provider_image')}
        imageTagId={providerGeneralTagData?.id}
        cols={4}
        rowHeight={240}
      />
      }
      <Divider className="my-8" />
      <BackdropCircular loading={uploadingProviderImage} />
    </div>
  );
};

export default AboutUsPageProviderBlock;
